import { useState, useRef, useEffect } from "react";
import { useApp } from "../../context/AppContext";
import { MobileLayout } from "../../components/MobileLayout";
import {
    CheckCircle2, Camera, X, RotateCcw, Loader2,
    Plus, Footprints, Clock,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { toast } from "sonner";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const BUCKET = "ronda-fotos";

async function uploadFoto(file, vigilanteId, pontoId) {
    try {
        const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
        const path = `${EMPRESA_ID}/${vigilanteId}/${Date.now()}_${pontoId}.${ext}`;
        const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
            contentType: file.type || "image/jpeg",
            upsert: false,
        });
        if (error) {
            console.error("[Storage] upload foto ERRO:", error.message);
            return null;
        }
        const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
        return data?.publicUrl || null;
    } catch (err) {
        console.error("[Storage] upload foto EXCEÇÃO:", err);
        return null;
    }
}

const fmtHora = (d) =>
    d instanceof Date
        ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
        : "—";

const fmtDuracao = (ms) => {
    const totalMin = Math.round(ms / 60000);
    if (totalMin < 60) return `${totalMin} min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

const MODAL_INITIAL = {
    view: "step",
    currentIndex: 0,
    photo: null,
    photoFile: null,
    observacao: "",
    confirmado: false,
    uploading: false,
};

export default function RondasMobile() {
    const { user, pontosDeRonda, scansQR, registrarScanQR } = useApp();

    const hoje = new Date().toDateString();
    const [rodadaIniciadaEm, setRodadaIniciadaEm] = useState(null);
    const [showModal, setShowModal]               = useState(false);
    const [modalState, setModalState]             = useState(MODAL_INITIAL);
    const [pontosTempos, setPontosTempos]         = useState([]); // { id, nome, confirmedAt: Date }

    const proxIndexRef    = useRef(-1);
    const fileInputRef    = useRef(null);
    const pontosTemposRef = useRef([]);

    // ── Chaves localStorage por vigilante + dia ───────────────────────────────
    const rodaKey   = user?.id ? `vc_rodada_${user.id}_${hoje}` : null;
    const pontosKey = user?.id ? `vc_rodada_pontos_${user.id}_${hoje}` : null;

    // Restaura progresso ao montar (scansQR já é persistido pelo AppContext)
    useEffect(() => {
        if (!rodaKey) return;
        const savedIso = localStorage.getItem(rodaKey);
        if (!savedIso) return;
        const dt = new Date(savedIso);
        if (isNaN(dt)) return;
        setRodadaIniciadaEm(dt);
        const savedPts = localStorage.getItem(pontosKey);
        if (savedPts) {
            try {
                const pts = JSON.parse(savedPts);
                const restored = pts.map(p => ({ ...p, confirmedAt: new Date(p.confirmedAt) }));
                setPontosTempos(restored);
                pontosTemposRef.current = restored;
            } catch { /* ignora JSON inválido */ }
        }
    }, []); // eslint-disable-line

    // Persiste rodadaIniciadaEm quando muda
    useEffect(() => {
        if (!rodaKey || !rodadaIniciadaEm) return;
        localStorage.setItem(rodaKey, rodadaIniciadaEm.toISOString());
    }, [rodaKey, rodadaIniciadaEm]);

    // Persiste pontosTempos quando muda
    useEffect(() => {
        if (!pontosKey || !pontosTempos.length) return;
        localStorage.setItem(pontosKey, JSON.stringify(
            pontosTempos.map(p => ({ ...p, confirmedAt: p.confirmedAt.toISOString() }))
        ));
    }, [pontosKey, pontosTempos]);

    // Scans da ronda atual
    const scansHoje = rodadaIniciadaEm === null ? [] : scansQR.filter(
        (s) =>
            s.vigilanteId === user?.id &&
            new Date(s.timestamp).toDateString() === hoje &&
            new Date(s.timestamp) >= rodadaIniciadaEm,
    );

    const total       = pontosDeRonda.length;
    const confirmados = pontosDeRonda.filter((p) =>
        scansHoje.some((s) => s.pontoId === p.id),
    ).length;
    const progresso   = total > 0 ? Math.round((confirmados / total) * 100) : 0;

    const primeiroNaoConcluido = pontosDeRonda.findIndex(
        (p) => !scansHoje.some((s) => s.pontoId === p.id),
    );

    // ── Abrir modal ──────────────────────────────────────────────────────────
    const openModal = () => {
        if (confirmados === total && total > 0) {
            setRodadaIniciadaEm(new Date());
            setPontosTempos([]);
            pontosTemposRef.current = [];
            setModalState({ ...MODAL_INITIAL, currentIndex: 0 });
        } else {
            const idx = primeiroNaoConcluido >= 0 ? primeiroNaoConcluido : 0;
            if (rodadaIniciadaEm === null) setRodadaIniciadaEm(new Date());
            setModalState({ ...MODAL_INITIAL, currentIndex: idx });
        }
        setShowModal(true);
    };

    const closeModal = () => setShowModal(false);

    // ── Câmera ───────────────────────────────────────────────────────────────
    const handlePhotoCapture = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setModalState((s) => ({ ...s, photo: URL.createObjectURL(file), photoFile: file }));
        e.target.value = "";
    };

    // ── Confirmar ponto ──────────────────────────────────────────────────────
    const confirmarPonto = async () => {
        const ponto = pontosDeRonda[modalState.currentIndex];
        const semFoto = !modalState.photoFile;

        const confirmedIds = new Set([...scansHoje.map((s) => s.pontoId), ponto.id]);
        const aFrente = pontosDeRonda.findIndex(
            (p, i) => i > modalState.currentIndex && !confirmedIds.has(p.id),
        );
        const proxIndex =
            aFrente !== -1
                ? aFrente
                : pontosDeRonda.findIndex(
                      (p, i) => i < modalState.currentIndex && !confirmedIds.has(p.id),
                  );
        proxIndexRef.current = proxIndex;

        setModalState((s) => ({ ...s, uploading: true }));

        let fotoUrl = null;
        try {
            fotoUrl = modalState.photoFile
                ? await uploadFoto(modalState.photoFile, user.id, ponto.id)
                : null;
        } catch (err) {
            console.error("[Ronda] erro inesperado no upload:", err);
        }

        if (semFoto) toast("Ponto confirmado sem foto", { duration: 3000 });

        const agora = new Date();
        const novosTempos = [...pontosTemposRef.current, { id: ponto.id, nome: ponto.nome, confirmedAt: agora }];
        pontosTemposRef.current = novosTempos;
        setPontosTempos(novosTempos);

        registrarScanQR(ponto.id, modalState.observacao, fotoUrl);
        setModalState((s) => ({ ...s, confirmado: true, uploading: false }));

        setTimeout(() => {
            const next = proxIndexRef.current;
            if (next !== -1) {
                setModalState({
                    view: "step",
                    currentIndex: next,
                    photo: null,
                    photoFile: null,
                    observacao: "",
                    confirmado: false,
                    uploading: false,
                });
            } else {
                setModalState((s) => ({ ...s, confirmado: false, view: "done" }));
                // Limpa progresso salvo (ronda concluída)
                if (rodaKey)   localStorage.removeItem(rodaKey);
                if (pontosKey) localStorage.removeItem(pontosKey);
                // Registra ronda completa na tabela liberty_rondas
                if (rodadaIniciadaEm && pontosTemposRef.current.length > 0) {
                    supabase.from("liberty_rondas").insert({
                        empresa_id:   EMPRESA_ID,
                        responsavel:  user?.nome || "Vigilante",
                        status:       "concluida",
                        iniciada_em:  rodadaIniciadaEm.toISOString(),
                        concluida_em: new Date().toISOString(),
                        pontos: pontosTemposRef.current.map((p) => ({
                            ponto_id:     p.id,
                            nome:         p.nome,
                            confirmado_em: p.confirmedAt.toISOString(),
                        })),
                    }).then(({ error }) => {
                        if (error) {
                            console.error("[Ronda] erro ao salvar ronda completa:", error.code, error.message);
                        }
                    });
                }
            }
        }, 1500);
    };

    const { view, currentIndex, photo, observacao, confirmado, uploading } = modalState;
    const ponto = pontosDeRonda[currentIndex];

    const labelBotao =
        confirmados === 0   ? "Iniciar Ronda"   :
        confirmados < total ? "Continuar Ronda" :
                              "Nova Ronda";

    // Cálculos de duração para a tela de conclusão
    const duracaoTotal = rodadaIniciadaEm && pontosTempos.length > 0
        ? pontosTempos[pontosTempos.length - 1].confirmedAt - rodadaIniciadaEm
        : 0;

    return (
        <MobileLayout title="Rondas">
            {/* ── Cabeçalho com progresso ─────────────────────────────────── */}
            <div className="rounded-2xl bg-gradient-to-br from-[#0d1b3e] to-[#0a1128] border border-white/10 p-5 mb-4">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <p className="tiny-label text-blue-200">Rondas do plantão</p>
                        <h2 className="font-display font-bold text-lg mt-1">Pontos de Ronda</h2>
                        <p className="text-xs text-slate-400 mt-1">Confirme cada ponto com foto</p>
                    </div>
                    <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                        <Footprints className="w-5 h-5 text-blue-300" />
                    </div>
                </div>

                <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-slate-400">{confirmados} de {total} pontos nesta ronda</span>
                    <span className="font-bold text-emerald-300">{progresso}%</span>
                </div>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                        style={{ width: `${progresso}%` }}
                    />
                </div>
            </div>

            {/* ── Botão principal ──────────────────────────────────────────── */}
            <button
                data-testid="btn-nova-ronda"
                onClick={openModal}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-emerald-500 text-black font-semibold active:scale-[0.98] transition"
            >
                <Plus className="w-5 h-5" />
                {labelBotao}
            </button>

            {/* ── Modal da ronda ────────────────────────────────────────────── */}
            {showModal && (
                <div
                    className="fixed inset-x-0 top-0 z-[55] bg-[#f4f5f8] flex flex-col"
                    style={{ bottom: "62px", fontFamily: "system-ui, -apple-system, sans-serif" }}
                >
                    {view === "done" ? (
                        /* ── Conclusão com resumo de tempo ── */
                        <div className="flex-1 flex flex-col overflow-y-auto px-5 py-6 gap-5">
                            <div className="flex flex-col items-center gap-3 text-center">
                                <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <CheckCircle2 className="w-10 h-10 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-[#333A56]">Ronda completa!</p>
                                    <p className="text-[#64748b] text-sm mt-1">
                                        {total} de {total} pontos registrados
                                    </p>
                                </div>
                                {duracaoTotal > 0 && (
                                    <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-200">
                                        <Clock className="w-4 h-4 text-emerald-600" />
                                        <span className="text-sm font-bold text-emerald-700">
                                            Duração total: {fmtDuracao(duracaoTotal)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Timeline dos pontos */}
                            {pontosTempos.length > 0 && (
                                <div className="rounded-2xl bg-white border border-[#e8eaf0] overflow-hidden">
                                    <div className="px-4 py-3 border-b border-[#e8eaf0]">
                                        <p className="text-[11px] font-semibold text-[#64748b] uppercase tracking-wide">
                                            Tempo por ponto
                                        </p>
                                    </div>
                                    {pontosTempos.map((p, i) => {
                                        const prev = i === 0 ? rodadaIniciadaEm : pontosTempos[i - 1].confirmedAt;
                                        const deltaMs = p.confirmedAt - prev;
                                        const deltaMin = Math.round(deltaMs / 60000);
                                        return (
                                            <div
                                                key={p.id}
                                                className="px-4 py-3 flex items-center gap-3 border-b border-[#f1f5f9] last:border-0"
                                            >
                                                <div className="w-7 h-7 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[13px] font-semibold text-[#333A56] truncate">{p.nome}</p>
                                                    <p className="text-[11px] text-[#64748b]">
                                                        {fmtHora(p.confirmedAt)}
                                                        {i === 0
                                                            ? <span className="ml-1 text-[#94a3b8]">(início)</span>
                                                            : <span className="ml-1 font-medium text-[#64748b]">
                                                                · +{deltaMin} min
                                                              </span>
                                                        }
                                                    </p>
                                                </div>
                                                <span className="text-[11px] font-mono text-[#94a3b8] shrink-0">
                                                    {String(i + 1).padStart(2, "0")}/{String(total).padStart(2, "0")}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="flex flex-col gap-2 mt-auto">
                                <button
                                    onClick={() => {
                                        if (rodaKey)   localStorage.removeItem(rodaKey);
                                        if (pontosKey) localStorage.removeItem(pontosKey);
                                        setRodadaIniciadaEm(new Date());
                                        setPontosTempos([]);
                                        pontosTemposRef.current = [];
                                        setModalState({ ...MODAL_INITIAL, currentIndex: 0 });
                                    }}
                                    className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-[#333A56] text-white font-bold text-sm active:scale-[0.98] transition"
                                >
                                    <RotateCcw className="w-4 h-4" />
                                    Nova Ronda
                                </button>
                                <button
                                    onClick={closeModal}
                                    className="w-full py-3.5 rounded-2xl bg-white border border-[#e8eaf0] text-[#333A56] font-semibold text-sm active:scale-[0.98] transition"
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* ── Passo a passo ── */
                        <>
                            {/* Header */}
                            <header className="flex-none bg-[#333A56] px-4 py-3 flex items-center justify-between">
                                <button
                                    onClick={closeModal}
                                    className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-white"
                                    aria-label="Fechar"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                                <div className="text-center flex-1 px-3">
                                    <p className="text-xs text-white/60 font-medium">
                                        {confirmados} de {total} concluídos
                                    </p>
                                    <div className="mt-1.5 w-full h-1 bg-white/20 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-white rounded-full transition-all duration-500"
                                            style={{ width: `${progresso}%` }}
                                        />
                                    </div>
                                </div>
                                <div className="w-9" />
                            </header>

                            {confirmado ? (
                                /* ── Flash de sucesso ── */
                                <div className="flex-1 flex flex-col items-center justify-center gap-5 text-center px-6">
                                    <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center">
                                        <CheckCircle2 className="w-12 h-12 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-emerald-700">Registrado!</p>
                                        <p className="text-sm text-[#64748b] mt-1">
                                            Avançando para o próximo…
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Conteúdo scrollável */}
                                    <div className="flex-1 flex flex-col px-4 pt-5 pb-4 gap-4 overflow-y-auto">
                                        {/* Nome do ponto */}
                                        <div className="text-center">
                                            <p className="text-xs font-semibold text-[#64748b] uppercase tracking-widest mb-2">
                                                Ponto {ponto?.ordem} de {total}
                                            </p>
                                            <h1 className="text-2xl font-bold text-[#333A56] leading-tight">
                                                {ponto?.nome}
                                            </h1>
                                            {/* Tempo desde o ponto anterior */}
                                            {pontosTempos.length > 0 && (
                                                <p className="text-xs text-[#64748b] mt-1 flex items-center justify-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    Último ponto: {fmtHora(pontosTempos[pontosTempos.length - 1].confirmedAt)}
                                                    {" · "}há {Math.round((Date.now() - pontosTempos[pontosTempos.length - 1].confirmedAt) / 60000)} min
                                                </p>
                                            )}
                                        </div>

                                        {/* Foto */}
                                        <div
                                            className="w-full rounded-3xl overflow-hidden border-2 border-dashed border-[#e8eaf0] bg-white flex items-center justify-center flex-1"
                                            style={{ minHeight: 200 }}
                                        >
                                            {photo ? (
                                                <img src={photo} alt="Foto do ponto" className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="flex flex-col items-center gap-3 py-10 text-[#64748b]">
                                                    <Camera className="w-14 h-14 text-[#e8eaf0]" />
                                                    <p className="text-sm">Tire uma foto do ponto</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Botões câmera */}
                                        <div className="flex gap-3">
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-[#333A56] text-white font-semibold text-sm active:scale-[0.98] transition"
                                            >
                                                <Camera className="w-5 h-5" />
                                                {photo ? "Refazer foto" : "Tirar foto"}
                                            </button>
                                            {photo && (
                                                <button
                                                    onClick={() => setModalState((s) => ({ ...s, photo: null, photoFile: null }))}
                                                    className="w-14 flex items-center justify-center rounded-2xl bg-white border border-[#e8eaf0] text-[#64748b] active:scale-[0.98] transition"
                                                >
                                                    <X className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>

                                        {/* Observação */}
                                        <textarea
                                            value={observacao}
                                            onChange={(e) => setModalState((s) => ({ ...s, observacao: e.target.value }))}
                                            placeholder="Observação opcional…"
                                            rows={2}
                                            className="w-full px-4 py-3 rounded-xl bg-white border border-[#e8eaf0] text-sm text-[#333A56] placeholder:text-[#64748b] focus:outline-none focus:border-[#333A56]/40 resize-none"
                                        />
                                    </div>

                                    {/* Botão confirmar — fixo na base do modal */}
                                    <div className="flex-none px-4 py-4 bg-[#f4f5f8] border-t border-[#e8eaf0]">
                                        {!photo && !uploading && (
                                            <p className="text-center text-[11px] text-[#94a3b8] mb-2">
                                                Foto opcional — você pode confirmar mesmo sem ela
                                            </p>
                                        )}
                                        <button
                                            onClick={confirmarPonto}
                                            disabled={uploading}
                                            className="w-full py-4 rounded-2xl bg-[#333A56] text-white font-bold text-base disabled:opacity-35 disabled:cursor-not-allowed active:scale-[0.98] transition flex items-center justify-center gap-2"
                                        >
                                            {uploading ? (
                                                <>
                                                    <Loader2 className="w-5 h-5 animate-spin" />
                                                    Enviando foto…
                                                </>
                                            ) : (
                                                `Confirmar ponto ${ponto?.ordem}`
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* Input câmera oculto */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handlePhotoCapture}
                    />
                </div>
            )}
        </MobileLayout>
    );
}
